import { apiSlice } from "./api";
import { useCallback } from 'react'; // Import useCallback

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    fetchPublicConfig: builder.query({
      query: () => ({
        url: `api/config`,
        method: "GET",
      }),
    }),
    login: builder.mutation({
      query: () => ({
        url: `api/user/me`,
        credentials: undefined,
        method: "GET",
      }),
    }),
    register: builder.mutation({
      query: (body) => ({
        url: `api/user/update/`,
        method: "POST",
        body: body,
      }),
    }),
    criteriaselection: builder.mutation({
      query: (body) => ({
        url: `api/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    generatePrompt: builder.mutation({
      query: (body) => {
        const formData = new FormData();
    
        Object.keys(body).forEach((key) => {
          if (Array.isArray(body[key])) {
            body[key].forEach(item => {
              if (item instanceof File) {
                // Append the File object as usual
                formData.append(key, item);
              } else if (typeof item === 'object' && item.previously_uploaded) {
                // Convert the metadata dictionary representing previously uploaded file to a JSON string
                const jsonString = JSON.stringify(item);
                // Create a Blob from the JSON string
                const jsonBlob = new Blob([jsonString], { type: 'application/json' });
                // Create a File object to include a filename
                const jsonFile = new File([jsonBlob], item.name, { type: 'application/json' });
                // Append the JSON file to the FormData
                formData.append(key, jsonFile);
              } else {
                // **Handle other items in the array as non-files**
                if (typeof item === 'object') {
                  formData.append(key, JSON.stringify(item));
                } else {
                  formData.append(key, item);
                }
              }
            });
          } else if (typeof body[key] === 'object' && !(body[key] instanceof File)) {
            // Stringify and append complex objects that are not File objects
            formData.append(key, JSON.stringify(body[key]));
          } else {
            // Append other data as is
            formData.append(key, body[key]);
          }
        });

        return {
          url: `api/generate/writing-prompt/`,
          method: "POST",
          body: formData, // Send formData instead of a JSON object
        };
      },
    }),
    generateText: builder.mutation({
      query: (body) => ({
        url: `api/generate/text/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    generateTextImage: builder.mutation({
      query: (body) => ({
        url: `api/generate/text-image/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    generateAssessmentBatch: builder.mutation({
      query: (body) => ({
        url: `api/generate/assessment-batch/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    generateTextLeveling: builder.mutation({
      query: (body) => ({
        url: `api/generate/text-leveling/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    gradeGeneralFeedback: builder.mutation({
      query: (body) => ({
        url: `api/grade/general-feedback/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    gradeRubricFeedback: builder.mutation({
      query: (body) => ({
        url: `api/grade/rubric-feedback/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    gradeCategoryFeedback: builder.mutation({
      query: (body) => ({
        url: `api/grade/category-feedback/`,
        method: "POST",
        body: { ...body },
      }),
    }),    
    gradeCalibration: builder.mutation({
      query: (body) => ({
        url: `api/grade/get-calibration/`,
        method: "POST",
        body: { ...body },
      }),
    }),    
    gradeCalibratedRubricFeedback: builder.mutation({
      query: (body) => ({
        url: `api/grade/calibrated-rubric-feedback/`,
        method: "POST",
        body: { ...body },
      }),
    }),    
    gradingTool: builder.mutation({
      query: (body) => ({
        url: `api/import-doc/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    recordText: builder.mutation({
      query: ({body, record_location}) => {
        const formData = new FormData();
    
        Object.keys(body).forEach((key) => {
          if (Array.isArray(body[key])) {
            body[key].forEach(item => {
              if (item instanceof File) {
                // Append the File object as usual
                formData.append(key, item);
              } else if (typeof item === 'object' && item.previously_uploaded) {
                // Convert the metadata dictionary representing previously uploaded file to a JSON string
                const jsonString = JSON.stringify(item);
                // Create a Blob from the JSON string
                const jsonBlob = new Blob([jsonString], { type: 'application/json' });
                // Create a File object to include a filename
                const jsonFile = new File([jsonBlob], item.name, { type: 'application/json' });
                // Append the JSON file to the FormData
                formData.append(key, jsonFile);
              } else {
                // **Handle other items in the array as non-files**
                if (typeof item === 'object') {
                  formData.append(key, JSON.stringify(item));
                } else {
                  formData.append(key, item);
                }
              }
            });
          } else if (typeof body[key] === 'object' && !(body[key] instanceof File)) {
            // Stringify and append complex objects that are not File objects
            formData.append(key, JSON.stringify(body[key]));
          } else {
            // Append other data as is
            formData.append(key, body[key]);
          }
        });

    
        return {
          url: `api/record/${record_location}/`,
          method: "POST",
          body: formData, // Send formData instead of a JSON object
        };
      },
    }),     
    evaluateAnswer: builder.mutation({
      query: (body) => ({
        url: `api/evaluate_answer/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    assessment: builder.mutation({
      query: (body) => ({
        url: `api/assessment/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    tasksCompleted: builder.mutation({
      query: (content_id) => ({
        url: `api/tasks_completed/?content_id=${content_id}`,
        method: "GET",
      }),
    }),
    survey: builder.mutation({
      query: (body) => ({
        url: `api/survey/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    feedback: builder.mutation({
      query: (body) => ({
        url: `api/feedback/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    saveExport: builder.mutation({
      query: (body) => ({
        url: `api/save-export/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    saveExportGrading: builder.mutation({
      query: (body) => ({
        url: `api/save-export-grading/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    saveExportTextLeveling: builder.mutation({
      query: (body) => ({
        url: `api/save-export-text-leveling/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    appUse: builder.mutation({
      query: (body) => ({
        url: `api/app_use/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    generateMoreQuestions: builder.mutation({
      query: (body) => ({
        url: `api/generate/more-questions/`,
        method: "POST",
        body: { ...body },
      }),
    }),
    getSippsWorksheetData: builder.mutation({
      query: (body) => ({
        url: `api/sipps-ws-data/`,
        method: "POST",
        body: { ...body },
      }),
      providesTags: ['SippsWorksheetData'],
    }),
    listGradingSessions: builder.query({
      query: () => 'api/grading-sessions/',
      providesTags: (result) =>
        result
          ? [
              ...result.sessions.map(({ id }) => ({ type: 'Session', id })),
              { type: 'Session', id: 'LIST' },
            ]
          : [{ type: 'Session', id: 'LIST' }],
    }),
    saveGradingSession: builder.mutation({
      query: (sessionData) => ({
        url: 'api/grading-sessions/save/',
        method: 'POST',
        body: sessionData,
      }),
      invalidatesTags: [{ type: 'Session', id: 'LIST' }],
    }),
    getGradingSession: builder.query({
      query: (sessionId) => `api/grading-sessions/${sessionId}/`,
    }),
    deleteGradingSession: builder.mutation({
      query: (sessionId) => ({
        url: `api/grading-sessions/${sessionId}/delete/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Session', id },
        { type: 'Session', id: 'LIST' },
      ],
    }),
    getGradingSessionData: builder.query({
      query: ({ assignment_id, rubric_id, sample_work_id }) => 
        `api/grading-session-data/?assignment_id=${assignment_id}&rubric_id=${rubric_id}&sample_work_id=${sample_work_id}`,
    }),
    downloadGradingSession: builder.mutation({
      query: (sessionId) => ({
        url: `api/grading-session-download/${sessionId}/`,
        method: 'GET',
      }),
    }),    
    fetchContributorBadges: builder.query({
      query: () => ({
        url: `api/contributors/badges`,
        method: "GET",
      }),
    }),
    fetchCityyearStats: builder.query({
      query: () => ({
        url: `api/cityyear-stats/`,
        method: "GET",
      }),
    }),
  }),
});

// Define the new custom Hook
export const useAppUsageLogger = () => {
  const [appUseMutationTrigger] = useAppUseMutation();

  const logAppUse = useCallback(async (from, to, handler = () => {}) => {
    try {
      const { data } = await appUseMutationTrigger({
        from,
        to,
      });
      if (handler && typeof handler === 'function') {
        handler(data);
      }
    } catch (err) {
      console.log({ err });
    }
  }, [appUseMutationTrigger]);

  return logAppUse;
};

export const {
  useFetchPublicConfigQuery,
  useLoginMutation,
  useRegisterMutation,
  useAssessmentMutation,
  useEvaluateAnswerMutation,
  useCriteriaselectionMutation,
  useGeneratePromptMutation,
  useGenerateTextMutation,
  useGenerateTextImageMutation,
  useGenerateAssessmentBatchMutation,
  useGenerateTextLevelingMutation,
  useRecordTextMutation,
  useTasksCompletedMutation,
  useSurveyMutation,
  useFeedbackMutation,
  useSaveExportMutation,
  useSaveExportGradingMutation,
  useSaveExportTextLevelingMutation,
  useAppUseMutation, // This is still exported for other potential uses, or can be removed if not used elsewhere
  useGenerateMoreQuestionsMutation,
  useGetSippsWorksheetDataMutation,
  useGradingToolMutation,
  useGradeGeneralFeedbackMutation,
  useGradeRubricFeedbackMutation,
  useGradeCategoryFeedbackMutation,
  useGradeCalibrationMutation,
  useGradeCalibratedRubricFeedbackMutation,
  useListGradingSessionsQuery,
  useSaveGradingSessionMutation,
  useGetGradingSessionQuery,
  useDeleteGradingSessionMutation,
  useGetGradingSessionDataQuery,
  useDownloadGradingSessionMutation,
  useFetchContributorBadgesQuery,
  useFetchCityyearStatsQuery,
} = authApiSlice;